import React, { useState, useEffect, useMemo } from "react";
import { DatePicker, Select, message, Modal } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Button, Upload } from "antd";
import { getRequest, postRequest } from '../../../../service';
import { getEmployeeId, getEmployeeData } from '../../../../utils/localStorage';
import { nowInUserTz, parseToUserTz, dayjs } from '../../../../utils/dateTime';

const getEmploymentStatus = (employeeData) => {
  const confirmationDate = employeeData?.p_confirmation_date;
  if (!confirmationDate) return "probation";
  return nowInUserTz().isBefore(parseToUserTz(confirmationDate), "day") ? "probation" : "permanent";
};

const filterLeaveConfigsByEmploymentStatus = (configs, employeeData) => {
  const status = getEmploymentStatus(employeeData);
  return configs.filter((cfg) => {
    const appliedOnly = cfg.leaveAppliedOnly || "permanent";
    return appliedOnly === status;
  });
};

const getApprovedLeaveDaysForYear = (leaveHistory = [], year) =>
  leaveHistory
    .filter((lh) => lh.status === "approved" && dayjs(lh.startDate).year() === year)
    .reduce((sum, lh) => sum + (parseFloat(lh.leaveDays) || 0), 0);

const getLeaveDaysUsedInMonth = (leaveHistory = [], year, month) =>
  leaveHistory
    .filter((lh) => {
      const leaveDate = dayjs(lh.startDate);
      return lh.status === "approved" && leaveDate.year() === year && leaveDate.month() === month;
    })
    .reduce((sum, lh) => sum + (parseFloat(lh.leaveDays) || 0), 0);

const getLeaveAccrualStartDate = (employeeData, leaveConfig) => {
  const appliedOnly = leaveConfig.leaveAppliedOnly || "permanent";
  if (appliedOnly === "permanent") {
    return employeeData?.p_confirmation_date || employeeData?.joiningDate;
  }
  return employeeData?.joiningDate;
};

const calculateProRatedLeaves = (totalLeaves, accrualStartDate, currentYear) => {
  if (!accrualStartDate) return totalLeaves;

  const startDate = dayjs(accrualStartDate);
  if (startDate.year() > currentYear) return 0;
  if (startDate.year() < currentYear) return totalLeaves;

  const remainingMonths = 12 - startDate.month();
  return Math.ceil((totalLeaves / 12) * remainingMonths);
};

const getMonthlyAllocation = (leaveConfig, proRatedTotal, accrualStartDate, currentYear) => {
  const configMonthly =
    leaveConfig.leavesAllowedPerMonth && leaveConfig.leavesAllowedPerMonth > 0
      ? leaveConfig.leavesAllowedPerMonth
      : null;

  if (accrualStartDate) {
    const startDate = dayjs(accrualStartDate);
    if (startDate.year() === currentYear) {
      const monthsFromStart = 12 - startDate.month();
      const proRatedMonthly = proRatedTotal / monthsFromStart;
      // Mid-year join: spread pro-rated total across remaining months, don't use
      // the full config monthly rate or the employee hits the year cap too early.
      return configMonthly !== null
        ? Math.min(configMonthly, proRatedMonthly)
        : proRatedMonthly;
    }
  }

  return configMonthly ?? proRatedTotal / 12;
};

const getLeaveEligibilityStartMonth = (accrualStartDate, currentYear) => {
  if (!accrualStartDate) return 0;
  const startDate = dayjs(accrualStartDate);
  if (startDate.year() > currentYear) return 12;
  if (startDate.year() === currentYear) return startDate.month();
  return 0;
};

const calculateLeaveBalance = (leaveConfig, employeeData, referenceDate = nowInUserTz()) => {
  const currentYear = referenceDate.year();
  const currentMonth = referenceDate.month();
  const accrualStartDate = getLeaveAccrualStartDate(employeeData, leaveConfig);
  const leaveHistory = leaveConfig.LeaveHistory || [];

  const proRatedTotal = calculateProRatedLeaves(
    leaveConfig.numberOfDays,
    accrualStartDate,
    currentYear
  );
  const monthlyAllocation = getMonthlyAllocation(
    leaveConfig,
    proRatedTotal,
    accrualStartDate,
    currentYear
  );
  const startMonth = getLeaveEligibilityStartMonth(accrualStartDate, currentYear);

  let carryForward = 0;
  for (let month = startMonth; month < currentMonth; month++) {
    const usedInMonth = getLeaveDaysUsedInMonth(leaveHistory, currentYear, month);
    carryForward += Math.max(0, monthlyAllocation - usedInMonth);
  }

  const usedInCurrentMonth = getLeaveDaysUsedInMonth(leaveHistory, currentYear, currentMonth);
  const currentMonthAvailable = Math.max(0, monthlyAllocation - usedInCurrentMonth);

  const yearUsed = getApprovedLeaveDaysForYear(leaveHistory, currentYear);
  const remaining = Math.max(0, proRatedTotal - yearUsed);

  carryForward = Math.min(carryForward, Math.max(0, remaining - currentMonthAvailable));
  const totalAvailable = Math.min(carryForward + currentMonthAvailable, remaining);

  return {
    proRatedTotal,
    monthlyAllocation,
    carryForward,
    currentMonthAvailable,
    totalAvailable,
    used: yearUsed,
    remaining,
    accrualStartDate,
    isProRated:
      !!accrualStartDate &&
      (dayjs(accrualStartDate).year() === currentYear ||
        dayjs(accrualStartDate).isAfter(dayjs(`${currentYear}-01-01`))),
    originalTotal: leaveConfig.numberOfDays,
  };
};

const LeaveRequestForm = () => {
  const [fileList, setFileList] = useState([]);
  const [leaveConfigs, setLeaveConfigs] = useState([]);
  const [formData, setFormData] = useState({
    leaveType: null,
    leaveDuration: "full",
    halfLeaveDuration: null,
    startDate: null,
    endDate: null,
    leaveDays: 0,
    reason: "",
    files: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [leaveTypeLimitReached, setLeaveTypeLimitReached] = useState(false);
  const [dateLimits, setDateLimits] = useState({ allowPast: 0, allowFuture: 0 });
  const [shiftData, setShiftData] = useState(null);
  const [remainingLeavesThisMonth, setRemainingLeavesThisMonth] = useState(0);
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeList, setEmployeeList] = useState();
  const employeeId = getEmployeeId();
  const employmentStatus = getEmploymentStatus(employeeData);

  const eligibleLeaveConfigs = useMemo(
    () => filterLeaveConfigsByEmploymentStatus(leaveConfigs, employeeData),
    [leaveConfigs, employeeData]
  );

  // Reset selected leave if it is no longer eligible
  useEffect(() => {
    if (
      formData.leaveType &&
      !eligibleLeaveConfigs.some((cfg) => cfg._id === formData.leaveType)
    ) {
      setFormData((prev) => ({
        ...prev,
        leaveType: null,
        leaveDuration: "full",
        halfLeaveDuration: null,
        startDate: null,
        endDate: null,
        leaveDays: 0,
        reason: "",
        files: [],
      }));
      setFileList([]);
    }
  }, [eligibleLeaveConfigs, formData.leaveType]);

  // Fetch employee data including joining date
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const userData = getEmployeeData();
        if (userData) {
          setEmployeeData(userData);
        }
      } catch (err) {
        console.error("Failed to get employee data:", err);
      }
    };
    fetchEmployeeData();
  }, []);

  // Fetch leave configs
  useEffect(() => {
    const fetchLeaveConfigs = async () => {
      try {
        const res = await getRequest('/api/leaves/byEmployee');
        if (res && Array.isArray(res.leaveConfigs)) {
          setLeaveConfigs(res.leaveConfigs);
        } else {
          setLeaveConfigs([]);
        }
      } catch (err) {
        message.error("Failed to fetch leave configs");
      }
    };
    fetchLeaveConfigs();
  }, []);

  // Fetch shift data
  useEffect(() => {
    const fetchShiftData = async () => {
      try {
        const res = await getRequest(`/api/companies/shifts/byEmployeeId?employeeId=${employeeId}`);
        if (res && res.shift) {
          setShiftData(res.shift);
        } else if (res) {
          setShiftData(res);
        }
      } catch (err) {
        message.error("Failed to fetch shift data");
      }
    };
    const fetchEmployeeList = async () => {
      try {
        const response = await getRequest(`/api/companies/teammembers?employeeId=${employeeId}`);
        if (response?.employee) {
          setEmployeeList(response);
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        // setLoading(false);
      }
    }
    if (employeeId) {
      fetchShiftData();
      fetchEmployeeList();
    }
  }, [employeeId]);

  // Calculate available leaves for current month including carry-forward
  const calculateAvailableLeavesForMonth = (leaveConfig, currentMonth, currentYear) => {
    if (!leaveConfig) return 0;

    const referenceDate = nowInUserTz().year(currentYear).month(currentMonth).date(1);
    return calculateLeaveBalance(leaveConfig, employeeData, referenceDate).totalAvailable;
  };

  // Check monthly leave limit and set date limits
  useEffect(() => {
    if (!formData.leaveType) {
      setLeaveTypeLimitReached(false);
      setDateLimits({ allowPast: 0, allowFuture: 0 });
      setRemainingLeavesThisMonth(0);
      return;
    }

    const selectedConfig = eligibleLeaveConfigs.find(cfg => cfg._id === formData.leaveType);
    if (!selectedConfig) return;

    const currentDate = formData.startDate ? dayjs(formData.startDate) : nowInUserTz();
    const currentMonth = currentDate.month();
    const currentYear = currentDate.year();

    // Calculate available leaves for current month including carry-forward
    const availableLeaves = calculateAvailableLeavesForMonth(selectedConfig, currentMonth, currentYear);

    setRemainingLeavesThisMonth(availableLeaves);

    if (availableLeaves <= 0) {
      setLeaveTypeLimitReached(true);
      message.error(
        `You have no remaining leaves for this leave type. All leaves have been used or allocated.`
      );
    } else {
      setLeaveTypeLimitReached(false);
    }

    setDateLimits({
      allowPast: selectedConfig.allowPastDates || 0,
      allowFuture: selectedConfig.allowFutureDates || 0,
    });
  }, [formData.leaveType, formData.startDate, eligibleLeaveConfigs, employeeData?.joiningDate, employeeData?.p_confirmation_date]);

  // DatePicker disabledDate logic
  const getDisabledDate = (current) => {
    const today = nowInUserTz().startOf('day');
    const minDate = today.subtract(dateLimits.allowPast, 'day');
    const maxDate = today.add(dateLimits.allowFuture, 'day');

    // For Half Day: disable dates from current date + 1
    if (formData.leaveDuration === 'half') {
      const tomorrow = today.add(0, 'day');
      if (current && current.isAfter(tomorrow, 'day')) {
        return true;
      }
    }

    // For date range limits
    if (current && (current.isBefore(minDate, 'day') || current.isAfter(maxDate, 'day'))) {
      return true;
    }

    // For non-working days
    if (current && !isWorkingDay(current)) {
      return true;
    }

    return false;
  };

  // Function to check if a date is a working day
  const isWorkingDay = (date) => {
    if (shiftData && shiftData.workingDays) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const currentDayName = dayNames[date.day()];
      return shiftData.workingDays.includes(currentDayName);
    }

    const selectedConfig = eligibleLeaveConfigs.find(cfg => cfg._id === formData.leaveType);
    const includeWeekends = selectedConfig?.includeWeekends || false;
    const isWeekend = date.day() === 0 || date.day() === 6;

    if (!includeWeekends && isWeekend) {
      return false;
    }

    return true;
  };

  // Function to count working days between two dates
  const countWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    let workingDays = 0;
    const totalDays = endDate.diff(startDate, 'day') + 1;

    for (let i = 0; i < totalDays; i++) {
      const currentDate = startDate.add(i, 'day');
      if (isWorkingDay(currentDate)) {
        workingDays++;
      }
    }

    return workingDays;
  };

  const calculateLeaveDays = (start, end, duration = formData.leaveDuration) => {
    if (!start || !end) {
      setFormData(prev => ({ ...prev, leaveDays: 0 }));
      return;
    }

    // Check if end date is before start date
    if (end.isBefore(start)) {
      setFormData(prev => ({ ...prev, leaveDays: 0 }));
      message.error('End date cannot be before start date');
      return;
    }

    // Check leave limits with carry-forward from previous months
    const selectedConfig = eligibleLeaveConfigs.find(cfg => cfg._id === formData.leaveType);
    if (selectedConfig) {
      const daysRequested = duration === 'half' ? 0.5 : countWorkingDays(start, end);

      if (daysRequested > remainingLeavesThisMonth) {
        message.error(`You can only take ${remainingLeavesThisMonth} more day(s) for this leave type (including unused leaves from previous months)`);
        setFormData(prev => ({ ...prev, leaveDays: 0 }));
        return;
      }
    }

    if (duration === 'half') {
      setFormData(prev => ({ ...prev, leaveDays: 0.5 }));
    } else if (start.isSame(end, 'day')) {
      setFormData(prev => ({ ...prev, leaveDays: isWorkingDay(start) ? 1 : 0 }));
    } else {
      setFormData(prev => ({ ...prev, leaveDays: countWorkingDays(start, end) }));
    }
  };

  const handleInputChange = (field, value) => {
    // Reset all fields when leaveType changes
    if (field === "leaveType") {
      setFormData({
        leaveType: value,
        leaveDuration: "full",
        halfLeaveDuration: null,
        startDate: null,
        endDate: null,
        leaveDays: 0,
        reason: "",
        files: [],
      });
      setFileList([]);
      return;
    }

    // Reset halfLeaveDuration when changing from half to full day
    if (field === "leaveDuration" && value === "full") {
      setFormData(prev => ({ ...prev, [field]: value, halfLeaveDuration: null }));
      return;
    }

    // For date changes
    if (field === "startDate" || field === "endDate") {
      const newFormData = { ...formData, [field]: value };

      // If endDate is before startDate, reset endDate
      if (field === "startDate" && newFormData.endDate && value && value.isAfter(newFormData.endDate)) {
        newFormData.endDate = null;
        newFormData.leaveDays = 0;
        message.error('End date cannot be before start date');
      }

      // If startDate is after endDate, reset startDate
      if (field === "endDate" && newFormData.startDate && value && value.isBefore(newFormData.startDate)) {
        newFormData.startDate = null;
        newFormData.leaveDays = 0;
        message.error('Start date cannot be after end date');
      }

      setFormData(newFormData);

      // Recalculate leave days if both dates exist
      if (newFormData.startDate && newFormData.endDate) {
        calculateLeaveDays(newFormData.startDate, newFormData.endDate, newFormData.leaveDuration);
      }
      return;
    }

    // For other fields
    setFormData(prev => ({ ...prev, [field]: value }));

    // Recalculate leave days when duration changes
    if (field === "leaveDuration" && formData.startDate && formData.endDate) {
      calculateLeaveDays(formData.startDate, formData.endDate, value);
    }
  };

  const uploadProps = {
    onRemove: (file) => {
      const newFileList = fileList.filter(f => f.uid !== file.uid);
      setFileList(newFileList);
      setFormData(prev => ({ ...prev, files: newFileList }));
    },
    beforeUpload: (file) => {
      const newList = [...fileList, file];
      setFileList(newList);
      setFormData(prev => ({ ...prev, files: newList }));
      return false;
    },
    fileList,
  };

  const handleSubmit = async () => {
    if (leaveTypeLimitReached) {
      message.error('You have reached the monthly limit for this leave type.');
      return;
    }

    if (!formData.leaveType || !formData.startDate || !formData.endDate) {
      message.error('Please fill all required fields');
      return;
    }

    if (formData.leaveDuration === 'half' && !formData.halfLeaveDuration) {
      message.error('Please select half day duration (First Half or Second Half)');
      return;
    }

    Modal.confirm({
      title: 'Confirm Leave Request',
      content: (
        <div>
          <p>Are you sure you want to submit this leave request?</p>
          <div className="mt-2 text-sm text-gray-600">
            <p><strong>Leave Type:</strong> {eligibleLeaveConfigs.find(cfg => cfg._id === formData.leaveType)?.title}</p>
            <p><strong>Duration:</strong> {formData.leaveDuration === 'full' ? 'Full Day' : 'Half Day'}</p>
            {formData.leaveDuration === 'half' && formData.halfLeaveDuration && (
              <p><strong>Half Day:</strong> {formData.halfLeaveDuration === 'firstHalf' ? 'First Half' : 'Second Half'}</p>
            )}
            <p><strong>From:</strong> {formData.startDate?.format('YYYY-MM-DD')}</p>
            <p><strong>To:</strong> {formData.endDate?.format('YYYY-MM-DD')}</p>
            <p><strong>Days:</strong> {formData.leaveDays}</p>
            {formData.reason && <p><strong>Reason:</strong> {formData.reason}</p>}
          </div>
        </div>
      ),
      okText: 'Submit',
      cancelText: 'Cancel',
      onOk: async () => {
        setSubmitting(true);
        try {
          const selectedConfig = eligibleLeaveConfigs.find(cfg => cfg._id === formData.leaveType);
          const payload = {
            ...formData,
            leaveTypeName: selectedConfig?.title || '',
            startDate: formData.startDate.format("YYYY-MM-DD"),
            endDate: formData.endDate.format("YYYY-MM-DD"),
            files: fileList.map(f => f.name),
            status: "pending"
          };

          const response = await postRequest('/api/leaveRequest', payload);
          if (response.status === 200 || response.message?.includes("successfully")) {
            const managementIds = (employeeList?.managementChain || []).map(m => m?._id).filter(Boolean);
            const uniqueReceiverIds = Array.from(new Set([employeeData?.companyId, ...managementIds].filter(Boolean)));
            const res = await postRequest('/api/notifications', {
              "title": `${employeeList.employee.name} Leave Request `,
              "senderId": employeeId,
              "receiverIds": uniqueReceiverIds,
              "type": "LeaveRequest",
              "relatedTypeId": response.leaveRequest._id,
              "relatedDocumentModel": "LeaveRequest",
              "companyId": employeeData?.companyId,
              "priority": "medium"
            });
            setFormData({
              leaveType: null,
              leaveDuration: "full",
              halfLeaveDuration: null,
              startDate: null,
              endDate: null,
              leaveDays: 0,
              reason: "",
              files: [],
            });
            setFileList([]);
            message.success('Leave request submitted successfully!');
          } else {
            message.error(response.error || 'Failed to submit leave request');
          }
        } catch (err) {
          message.error('Failed to submit leave request');
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const leaveSummary = eligibleLeaveConfigs.map((cfg) => {
    const balance = calculateLeaveBalance(cfg, employeeData);

    return {
      name: cfg.title,
      totalAllowed: balance.proRatedTotal,
      used: balance.used,
      remaining: balance.remaining,
      originalTotal: balance.originalTotal,
      isProRated: balance.isProRated,
      monthlyAllocation: balance.monthlyAllocation,
      previousMonthsUnused: balance.carryForward,
      currentMonthAvailable: balance.currentMonthAvailable,
      totalAvailable: balance.totalAvailable,
    };
  });

  return (
    <div className="flex flex-col lg:flex-row gap-8 p-4 w-full bg-white">
      {/* Form Section */}
      <div className="w-full lg:w-3/5 space-y-4">
        <h4 className="text-2xl font-semibold mb-2">Add leave request</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="text-md text-gray-700">
            Leave Type
            <Select
              className="w-full"
              placeholder="Select One"
              value={formData.leaveType}
              options={eligibleLeaveConfigs.map(cfg => ({
                value: cfg._id,
                label: cfg.title,
                disabled: leaveTypeLimitReached && cfg._id === formData.leaveType
              }))}
              onChange={val => handleInputChange("leaveType", val)}
              notFoundContent={
                eligibleLeaveConfigs.length === 0
                  ? `No leave types available for ${employmentStatus} employees`
                  : undefined
              }
            />
          </label>

          <label className="text-md text-gray-700">
            Leave Duration
            <Select
              className="w-full"
              value={formData.leaveDuration}
              options={[
                { value: 'full', label: 'Full Day' },
                { value: 'half', label: 'Half Day' },
              ]}
              onChange={val => handleInputChange("leaveDuration", val)}
            />
          </label>

          {formData.leaveDuration === 'half' && (
            <label className="text-md text-gray-700">
              Half Day Duration
              <Select
                className="w-full"
                value={formData.halfLeaveDuration}
                options={[
                  { value: 'firstHalf', label: 'First Half' },
                  { value: 'secondHalf', label: 'Second Half' },
                ]}
                onChange={val => handleInputChange("halfLeaveDuration", val)}
                placeholder="Select half day duration"
              />
            </label>
          )}

          <label className="text-md text-gray-700">
            Leave From
            <DatePicker
              onChange={date => handleInputChange("startDate", date)}
              className="w-full"
              value={formData.startDate}
              disabledDate={getDisabledDate}
              disabled={!formData.leaveType}
            />
          </label>

          <label className="text-md text-gray-700">
            Leave To
            <DatePicker
              onChange={date => handleInputChange("endDate", date)}
              className="w-full"
              value={formData.endDate}
              disabledDate={current => {
                if (!formData.startDate) return true;
                return getDisabledDate(current) ||
                  (formData.leaveDuration === 'half' && !current.isSame(formData.startDate, 'day'));
              }}
              disabled={!formData.startDate}
            />
          </label>

          <label className="text-md text-gray-700">
            Leave Days
            <input
              type="text"
              value={formData.leaveDays}
              className="block w-full border border-gray-400 p-1.5 rounded-md bg-gray-200"
              disabled
            />
          </label>
        </div>

        <label className="text-md text-gray-700">
          Reason
          <textarea
            className="block w-full border border-gray-400 p-1.5 rounded-md"
            rows="6"
            value={formData.reason}
            onChange={e => handleInputChange("reason", e.target.value)}
          />
        </label>

        <div className="flex items-center gap-1.5 mt-3">
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <Button onClick={() => window.history.back()}>
            Cancel
          </Button>
          <Button
            className="bg-primary text-white"
            type="primary"
            onClick={handleSubmit}
            disabled={submitting || leaveTypeLimitReached || formData.leaveDays <= 0}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </div>

        {leaveTypeLimitReached && (
          <div className="text-red-500 mt-2 p-3 bg-red-50 rounded-lg">
            <strong>Leave Limit Reached:</strong> You have reached the monthly limit for this leave type and have no remaining leaves.
          </div>
        )}
        {remainingLeavesThisMonth > 0 && !leaveTypeLimitReached && (
          <div className="text-green-500 mt-2 p-3 bg-green-50 rounded-lg">
            <strong>Available Leaves:</strong> You can take up to {remainingLeavesThisMonth.toFixed(1)} more day(s) for this leave type (including unused leaves from previous months).
          </div>
        )}

      </div>

      {/* Summary Section */}
      <div className="w-full lg:w-2/5">
        <h4 className="text-2xl font-semibold mb-2">
          Employee Leave Summary (Current Year)
        </h4>

        {/* Employment Status Info */}
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Employment Status:</strong>{" "}
            {employmentStatus === "permanent" ? "Permanent" : "Probation"}
            {employeeData?.p_confirmation_date && (
              <span className="block text-xs text-blue-600 mt-1">
                Confirmation Date: {dayjs(employeeData.p_confirmation_date).format("DD/MM/YYYY")}
              </span>
            )}
          </p>
        </div>

        <div className="overflow-x-auto mt-4">
          <table className="w-full text-md">
            <thead>
              <tr className="bg-primary text-white">
                <th className="p-1.5 rounded-tl-md">Name</th>
                <th className="p-1.5">Total</th>
                <th className="p-1.5">Used</th>
                <th className="p-1.5">Rem</th>
                <th className="p-1.5 rounded-tr-md">Available</th>
              </tr>
            </thead>
            <tbody>
              {leaveSummary.map((leave, index) => (
                <tr key={index}>
                  <td className="border border-gray-400 p-1.5 rounded-tl-md">
                    {leave.name}
                    {leave.isProRated && (
                      <span className="block text-xs text-gray-500">
                        (Pro-rated)
                      </span>
                    )}
                  </td>
                  <td className="border border-gray-400 p-1.5">
                    {leave.totalAllowed.toFixed(1)}
                    {leave.isProRated && leave.originalTotal !== leave.totalAllowed && (
                      <span className="block text-xs text-gray-500">
                        of {leave.originalTotal.toFixed(1)}
                      </span>
                    )}
                  </td>
                  <td className="border border-gray-400 p-1.5">{leave.used.toFixed(1)}</td>
                  <td className="border border-gray-400 p-1.5">{leave.remaining.toFixed(1)}</td>
                  <td className="border border-gray-400 p-1.5 rounded-tr-md">
                    <div className="text-center">
                      <span className="font-semibold text-green-600">{leave.totalAvailable.toFixed(1)}</span>
                      <div className="text-xs text-gray-500">
                        <div>Monthly: {leave.monthlyAllocation.toFixed(1)}</div>
                        <div>Carry: {leave.previousMonthsUnused.toFixed(1)}</div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Monthly Leave Info */}
        <div className="mt-4 p-3 bg-green-50 rounded-lg">
          <p className="text-sm text-green-800">
            <strong>Monthly Leave Policy:</strong>
          </p>
          <div className="text-xs text-green-700 mt-1 space-y-1">
            <p>• Each month gets {leaveSummary.find(l => l.name === eligibleLeaveConfigs.find(cfg => cfg._id === formData.leaveType)?.title)?.monthlyAllocation?.toFixed(1) || '0'} leave(s)</p>
            <p>• Unused leaves from previous months are carried forward</p>
            <p>• Current month: {leaveSummary.find(l => l.name === eligibleLeaveConfigs.find(cfg => cfg._id === formData.leaveType)?.title)?.currentMonthAvailable?.toFixed(1) || '0'} leave(s)</p>
            <p>• Carry-forward: {leaveSummary.find(l => l.name === eligibleLeaveConfigs.find(cfg => cfg._id === formData.leaveType)?.title)?.previousMonthsUnused?.toFixed(1) || '0'} leave(s)</p>
            <p>• <strong>Total Available Now: {remainingLeavesThisMonth.toFixed(1)} leave(s)</strong></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveRequestForm;